import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge
} from '../../components/UI';
import apiClient from '../../utils/api';
import {
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle,
  ArrowLeft, ArrowRight, Save, RefreshCw, Loader2, DollarSign,
  ChevronDown, ChevronUp, Edit3, X
} from 'lucide-react';

const BudgetV2ExcelUpload = ({ budgetId, budgetName, fiscalYear, onBack, onComplete }) => {
  const navigate = useNavigate();

  // Upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Parsed data state
  const [parsedData, setParsedData] = useState(null);
  const [validation, setValidation] = useState(null);

  // Mapping state
  const [mapping, setMapping] = useState(false);
  const [mappedData, setMappedData] = useState(null);
  const [mappingValidation, setMappingValidation] = useState(null);

  // Saving state
  const [saving, setSaving] = useState(false);

  // UI state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [step, setStep] = useState(1); // 1: Upload, 2: Review Parsed, 3: Review Mapping, 4: Save
  const [removedCategories, setRemovedCategories] = useState(new Set()); // Track removed category indices

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        setUploadError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setUploadError(null);
      setParsedData(null);
      setMappedData(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.match(/\.(xlsx|xls)$/i)) {
      setFile(droppedFile);
      setUploadError(null);
      setParsedData(null);
      setMappedData(null);
    } else {
      setUploadError('Please drop an Excel file (.xlsx or .xls)');
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const uploadAndParse = async () => {
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/api/budget-v2/budgets/${budgetId}/upload-excel`,
        formData
      );

      if (response.success) {
        setParsedData(response.parsed_data);
        setValidation(response.validation);
        setRemovedCategories(new Set()); // Reset removed categories on new upload
        setStep(2);
      } else {
        setUploadError(response.error || 'Failed to parse Excel file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.detail || error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const mapToNetSuite = async () => {
    if (!parsedData) return;

    setMapping(true);
    setUploadError(null);

    try {
      // Filter out removed categories before sending to AI mapping
      const filteredParsedData = {
        ...parsedData,
        categories: parsedData.categories.filter((_, idx) => !removedCategories.has(idx))
      };

      const response = await apiClient.post(
        `/api/budget-v2/budgets/${budgetId}/map-excel`,
        filteredParsedData
      );

      if (response.success) {
        setMappedData(response.mapped_data);
        setMappingValidation(response.validation);
        setStep(3);
      } else {
        setUploadError(response.error || 'Mapping failed');
      }
    } catch (error) {
      console.error('Mapping error:', error);
      setUploadError(error.response?.data?.detail || error.message || 'Mapping failed');
    } finally {
      setMapping(false);
    }
  };

  const saveBudget = async () => {
    if (!mappedData) return;

    setSaving(true);
    setUploadError(null);

    try {
      const response = await apiClient.post(
        `/api/budget-v2/budgets/${budgetId}/save-mapped`,
        mappedData
      );

      if (response.success) {
        setStep(4);
        // Navigate to budget detail after 2 seconds
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          } else {
            navigate(`/budget-agent-v2/${budgetId}`);
          }
        }, 2000);
      } else {
        setUploadError(response.error || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      setUploadError(error.response?.data?.detail || error.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (idx) => {
    setExpandedCategories(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const removeCategory = (idx) => {
    setRemovedCategories(prev => {
      const newSet = new Set(prev);
      newSet.add(idx);
      return newSet;
    });
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high':
        return <Badge className="bg-emerald-100 text-emerald-700">High Match</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-700">Medium Match</Badge>;
      case 'low':
        return <Badge className="bg-orange-100 text-orange-700">Low Match</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-700">No Match</Badge>;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Header */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-500" />
                  AI Budget Import
                </h2>
                <p className="text-sm text-gray-500">
                  {budgetName} | FY {fiscalYear}
                </p>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {uploadError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{uploadError}</span>
          <button
            onClick={() => setUploadError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Upload Excel Budget File</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                file
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50'
              }`}
            >
              {file ? (
                <div className="space-y-4">
                  <FileSpreadsheet className="w-16 h-16 mx-auto text-emerald-500" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setFile(null)}
                    >
                      Change File
                    </Button>
                    <Button
                      onClick={uploadAndParse}
                      disabled={uploading}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload & Parse
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="w-16 h-16 mx-auto text-gray-400" />
                  <div>
                    <p className="text-lg font-medium text-gray-700">
                      Drag & drop your Excel file here
                    </p>
                    <p className="text-sm text-gray-500">or click to browse</p>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label
                    htmlFor="excel-upload"
                    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Select Excel File
                  </label>
                </div>
              )}
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Supported Format</h4>
              <p className="text-sm text-blue-700">
                Upload your budget Excel file with categories and monthly amounts.
                The system will automatically detect and parse budget categories like
                "Total - 60000 - Technology Expense" with monthly budget columns.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Review Parsed Data */}
      {step === 2 && parsedData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Step 2: Review Parsed Data</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700">
                  {(parsedData.categories?.length || 0) - removedCategories.size} Categories
                  {removedCategories.size > 0 && ` (${removedCategories.size} removed)`}
                </Badge>
                <Badge className="bg-blue-100 text-blue-700">
                  {parsedData.metadata?.months_found?.join(', ')}
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Validation Warnings */}
            {validation?.warnings?.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Warnings:</span>
                </div>
                <ul className="mt-2 text-sm text-amber-600 list-disc list-inside">
                  {validation.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Financial Summary - All months in $M */}
            <div className="mb-6 space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-emerald-800">Income Budget ($M)</h4>
                  <span className="text-sm font-bold text-emerald-700">
                    Total: {(Object.values(parsedData.income || {}).reduce((s, v) => s + (v || 0), 0) / 1000000).toFixed(2)}M
                  </span>
                </div>
                <div className="grid grid-cols-6 gap-2 text-sm">
                  {parsedData.metadata?.months_found?.map((month) => (
                    <div key={month} className="text-center">
                      <span className="text-gray-500 text-xs block">{month}</span>
                      <span className="font-medium">
                        {((parsedData.income?.[month.toLowerCase()] || 0) / 1000000).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-orange-800">COGS Budget ($M)</h4>
                  <span className="text-sm font-bold text-orange-700">
                    Total: {(Object.values(parsedData.cogs || {}).reduce((s, v) => s + (v || 0), 0) / 1000000).toFixed(2)}M
                  </span>
                </div>
                <div className="grid grid-cols-6 gap-2 text-sm">
                  {parsedData.metadata?.months_found?.map((month) => (
                    <div key={month} className="text-center">
                      <span className="text-gray-500 text-xs block">{month}</span>
                      <span className="font-medium">
                        {((parsedData.cogs?.[month.toLowerCase()] || 0) / 1000000).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Categories Table - All months with horizontal scroll */}
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-max">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-center font-medium text-gray-700 w-10"></th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50">Category</th>
                    {parsedData.metadata?.months_found?.map((month) => (
                      <th key={month} className="px-3 py-2 text-right font-medium text-gray-700 whitespace-nowrap">
                        {month} ($M)
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium text-gray-700 whitespace-nowrap">Total ($M)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {parsedData.categories?.map((cat, idx) => (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-50 ${removedCategories.has(idx) ? 'opacity-40 bg-gray-100' : ''}`}
                    >
                      <td className="px-2 py-2 text-center">
                        {!removedCategories.has(idx) ? (
                          <button
                            onClick={() => removeCategory(idx)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Remove category"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Removed</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap">
                        {cat.name}
                        {cat.account_number && <span className="text-xs text-gray-400 ml-1">({cat.account_number})</span>}
                        {cat.total === 0 && <span className="text-xs text-amber-500 ml-2">($0 budget)</span>}
                      </td>
                      {parsedData.metadata?.months_found?.map((month) => (
                        <td key={month} className="px-3 py-2 text-right text-gray-700">
                          {((cat.monthly_budgets?.[month.toLowerCase()] || 0) / 1000000).toFixed(2)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-medium text-emerald-600">
                        {((cat.total || 0) / 1000000).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Removed count info */}
            {removedCategories.size > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                {removedCategories.size} categor{removedCategories.size === 1 ? 'y' : 'ies'} removed from import
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={mapToNetSuite}
                disabled={mapping}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {mapping ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Mapping to NetSuite...
                  </>
                ) : (
                  <>
                    Map to NetSuite Accounts
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review Mapping */}
      {step === 3 && mappedData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Step 3: Review NetSuite Mapping</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-700">
                  {mappedData.summary?.matched_high || 0} High Match
                </Badge>
                <Badge className="bg-amber-100 text-amber-700">
                  {mappedData.summary?.matched_medium || 0} Medium
                </Badge>
                {mappedData.summary?.unmatched > 0 && (
                  <Badge className="bg-red-100 text-red-700">
                    {mappedData.summary?.unmatched} Unmatched
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mapping Summary */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {mappedData.summary?.total_categories || 0}
                  </p>
                  <p className="text-sm text-gray-500">Total Categories</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    {mappedData.summary?.matched_high || 0}
                  </p>
                  <p className="text-sm text-gray-500">High Confidence</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">
                    {mappedData.summary?.matched_medium || 0}
                  </p>
                  <p className="text-sm text-gray-500">Medium Confidence</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {mappedData.summary?.unmatched || 0}
                  </p>
                  <p className="text-sm text-gray-500">Unmatched</p>
                </div>
              </div>
            </div>

            {/* Mapped Categories */}
            <div className="space-y-3">
              {mappedData.mapped_categories?.map((cat, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg overflow-hidden ${
                    cat.netsuite_match?.confidence === 'high'
                      ? 'border-emerald-200'
                      : cat.netsuite_match?.confidence === 'medium'
                      ? 'border-amber-200'
                      : 'border-red-200'
                  }`}
                >
                  <div
                    className={`p-4 cursor-pointer ${
                      cat.netsuite_match?.confidence === 'high'
                        ? 'bg-emerald-50'
                        : cat.netsuite_match?.confidence === 'medium'
                        ? 'bg-amber-50'
                        : 'bg-red-50'
                    }`}
                    onClick={() => toggleCategory(idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {cat.netsuite_match ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {cat.account_number ? `${cat.account_number} - ` : ''}{cat.name}
                          </p>
                          {cat.netsuite_match && (
                            <p className="text-sm text-gray-500">
                              → {cat.netsuite_match.account_number}: {cat.netsuite_match.account_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getConfidenceBadge(cat.netsuite_match?.confidence)}
                        <span className="font-medium text-gray-700">
                          {formatCurrency(cat.total)}
                        </span>
                        {expandedCategories[idx] ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedCategories[idx] && (
                    <div className="p-4 bg-white border-t">
                      <div className="grid grid-cols-6 gap-4 text-sm">
                        {Object.entries(cat.monthly_budgets || {}).map(([month, amount]) => (
                          <div key={month}>
                            <span className="text-gray-500 capitalize">{month}:</span>
                            <span className="ml-1 font-medium">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={saveBudget}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving Budget...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Budget
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-20 h-20 mx-auto text-emerald-500 mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Budget Saved Successfully!
            </h2>
            <p className="text-gray-500 mb-6">
              Your budget has been created with {mappedData?.mapped_categories?.length || 0} categories.
              Redirecting to budget details...
            </p>
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-emerald-500" />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BudgetV2ExcelUpload;
